import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, ShieldCheck, KeyRound, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleContext";
import { toast } from "sonner";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_trafficking-intel-1/artifacts/art1f2uo_Screenshot%202026-06-27%20at%208.10.47%E2%80%AFAM.png";

export default function Splash() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useLocale();
  const [guestLoading, setGuestLoading] = useState(false);

  const onGuestDemo = async () => {
    if (guestLoading) return;
    setGuestLoading(true);
    try {
      await login("demo@mukti.local", "Mukti-Demo-2026!");
      toast.success("Signed in as demo investigator");
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Demo sign-in failed");
      setGuestLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen relative overflow-hidden bg-slate-50"
      data-testid="splash-page"
    >
      {/* Decorative dawn gradient */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(1200px 600px at 20% -10%, rgba(251,146,60,0.18), transparent 60%), radial-gradient(900px 500px at 100% 110%, rgba(30,58,138,0.18), transparent 55%)",
        }}
      />

      {/* Subtle grid noise */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(#0F172A 1px, transparent 1px), linear-gradient(90deg, #0F172A 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      />

      {/* Top bar */}
      <header className="splash-fade-in relative z-10 px-6 sm:px-10 py-5 flex items-start justify-between gap-4" style={{ animationDelay: "0ms" }}>
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2 text-slate-500 text-xs font-mono uppercase tracking-[0.18em]">
            <ShieldCheck className="h-4 w-4 text-blue-700" />
            <span>Project Mukti · MVP v2.0</span>
          </div>
          <div
            data-testid="splash-language-support"
            className="text-[11px] text-slate-500 font-mono tracking-wide pl-6"
          >
            Works in English / ಕನ್ನಡ / हिंदी / मराठी
          </div>
        </div>
        <div className="text-xs text-slate-400 font-mono pt-1" data-testid="splash-region-label">Karnataka · EN</div>
      </header>

      {/* Hero */}
      <main className="relative z-10 px-6 sm:px-10">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-[1.05fr_0.95fr] gap-10 items-center pt-6 lg:pt-12">
          {/* Left: Copy */}
          <div className="order-2 lg:order-1">
            <div
              className="splash-fade-in inline-flex items-center gap-2 border border-orange-200 bg-orange-50 text-orange-800 text-[11px] font-mono uppercase tracking-[0.18em] px-3 py-1 rounded-full"
              style={{ animationDelay: "600ms" }}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-orange-500" />
              Anti-Trafficking Intelligence
            </div>

            <h1
              className="splash-fade-in font-display font-bold tracking-tight text-slate-900 mt-5 text-4xl sm:text-5xl lg:text-6xl leading-[1.02]"
              style={{ animationDelay: "750ms" }}
            >
              <span className="block">Information.</span>
              <span className="block">Intelligence.</span>
              <span className="block text-orange-600">Freedom.</span>
            </h1>

            <p
              className="splash-fade-in mt-6 text-slate-600 text-base sm:text-lg leading-relaxed max-w-xl"
              style={{ animationDelay: "950ms" }}
            >
              Mukti turns scattered signals from public advertisements into
              explainable investigative leads — so trained human investigators
              can move faster against trafficking networks in Bangalore.
            </p>

            <div
              className="splash-fade-in mt-8 flex flex-wrap items-center gap-3"
              style={{ animationDelay: "1150ms" }}
            >
              <Button
                data-testid="splash-start-btn"
                onClick={() => navigate("/login")}
                className="splash-pulse group bg-slate-900 hover:bg-slate-800 text-white h-12 px-7 text-base rounded-md gap-2"
              >
                Start
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Button>

              <button
                type="button"
                data-testid="splash-guest-btn"
                onClick={onGuestDemo}
                disabled={guestLoading}
                className="inline-flex items-center gap-2 text-sm text-slate-700 hover:text-slate-900 underline-offset-4 hover:underline disabled:opacity-60"
              >
                {guestLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <KeyRound className="h-4 w-4" />
                )}
                {guestLoading ? "Signing in…" : "Continue as guest demo"}
              </button>
            </div>

            <div
              className="splash-fade-in mt-3 text-xs text-slate-500 font-mono uppercase tracking-wider"
              style={{ animationDelay: "1300ms" }}
            >
              {t("splash.investigativeUse")}
            </div>

            <div
              className="splash-fade-in mt-5 text-sm text-slate-700"
              style={{ animationDelay: "1350ms" }}
            >
              {t("splash.citizenCta")}{" "}
              <a
                href="/citizen/report"
                data-testid="splash-citizen-link"
                className="text-orange-700 underline underline-offset-4 hover:text-orange-800"
              >
                {t("splash.reportAnon")}
              </a>
            </div>

            <div
              className="splash-fade-in mt-10 grid grid-cols-3 gap-4 max-w-md"
              style={{ animationDelay: "1450ms" }}
            >
              {[
                { k: "15+", v: "Signals" },
                { k: "0", v: "Conclusions" },
                { k: "100%", v: "Human review" },
              ].map((s) => (
                <div
                  key={s.v}
                  className="border-l border-slate-200 pl-3"
                  data-testid={`splash-stat-${s.v.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <div className="font-display font-bold text-2xl text-slate-900">
                    {s.k}
                  </div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono mt-0.5">
                    {s.v}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Logo */}
          <div className="order-1 lg:order-2 flex justify-center lg:justify-end">
            <div className="relative">
              <div
                aria-hidden
                className="absolute -inset-10 rounded-full splash-halo"
                style={{
                  background:
                    "radial-gradient(closest-side, rgba(251,146,60,0.30), transparent 70%)",
                }}
              />
              <img
                src={LOGO_URL}
                alt="Mukti — Together against human trafficking"
                className="splash-logo-enter relative w-[280px] sm:w-[360px] lg:w-[460px] aspect-square object-contain"
                data-testid="splash-logo"
              />
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer
        className="splash-fade-in relative z-10 px-6 sm:px-10 py-6 mt-8"
        style={{ animationDelay: "1600ms" }}
      >
        <div className="max-w-6xl mx-auto border-t border-slate-200 pt-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <p className="text-xs text-slate-500 max-w-2xl leading-relaxed">
            Mukti does not identify individuals as criminals or make legal
            conclusions. All findings are leads requiring human review.
          </p>
          <p className="text-[11px] font-mono uppercase tracking-wider text-slate-400">
            Together against human trafficking
          </p>
        </div>
      </footer>

      <style>{`
        @keyframes splash-fade {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .splash-fade-in {
          opacity: 0;
          animation: splash-fade 700ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }

        @keyframes splash-logo {
          0%   { opacity: 0; transform: scale(0.86) translateY(14px); filter: blur(8px); }
          60%  { opacity: 1; transform: scale(1.02) translateY(0);    filter: blur(0); }
          100% { opacity: 1; transform: scale(1)    translateY(0);    filter: blur(0); }
        }
        .splash-logo-enter {
          opacity: 0;
          animation: splash-logo 1200ms cubic-bezier(0.22, 1, 0.36, 1) 150ms forwards;
        }

        @keyframes splash-halo-anim {
          0%   { opacity: 0;   transform: scale(0.85); }
          70%  { opacity: 1;   transform: scale(1.05); }
          100% { opacity: 0.9; transform: scale(1); }
        }
        .splash-halo {
          opacity: 0;
          animation: splash-halo-anim 1400ms ease-out 100ms forwards;
        }

        @keyframes splash-pulse-anim {
          0%, 100% { box-shadow: 0 0 0 0 rgba(15, 23, 42, 0.45); }
          50%      { box-shadow: 0 0 0 12px rgba(15, 23, 42, 0); }
        }
        .splash-pulse {
          animation: splash-pulse-anim 2200ms ease-out 1800ms 3;
        }
      `}</style>
    </div>
  );
}
